<script>
import { getAllBadgeConfigs$Cache } from '@/datas/auto-expire-badge';
import { t } from '@/plugins/i18n';

export default {
  name: 'AutoExpireBadge',
  props: {
    /**
     * Unique ID for each feature/plan
     *
     * Note: need to be consistent with the BE configuration enum value
     */
    badgeId: {
      type: String,
      required: false,
    },
    /**
     * feature | plan | comparison
     */
    theme: {
      type: String,
      require: false,
    },
    content: {
      type: String,
      require: false,
    },
  },
  data() {
    return {
      notExpiredList: [],
    };
  },
  async created() {
    if (!this.badgeId) {
      return;
    }
    try {
      const res = await getAllBadgeConfigs$Cache();
      this.notExpiredList = res;
    } catch (err) {
      console.error(`AutoExpireBadge - Error to get badge list for [${this.badgeId}]: `, err);
    }
  },
  computed: {
    badgeInfo() {
      if (!this.badgeId) {
        return undefined;
      }
      return this.notExpiredList.find((item) => {
        return item.tagKeys.includes(this.badgeId);
      });
    },
    isActive() {
      if (this.content) {
        return true;
      }

      if (!this.badgeInfo) {
        return false;
      }

      const pages = this.badgeInfo.pages || [];
      return pages.some(path => {
        const reg = new RegExp(path, 'ig');
        return path === 'all' || reg.test(location.pathname)
      });
    },
    badgeThemeClass() {
      if (this.theme) {
        return `badge--${this.theme}`;
      } else {
        return 'badge--feature'
      }
    },
    badgeLabel() {
      if (this.content) {
        return this.content;
      } else {
        const tag = this.badgeInfo?.tagType?.toLocaleLowerCase();
        switch (tag) {
          case 'new':
            return this._$t('fbc.common.tag.new');
          case 'popular':
            return this._$t('fbc.common.tag.popular');
          case 'recommended':
            return this._$t('fbc.common.tag.recommended');
          default:
            return this._$t('fbc.common.tag.new');
        }
      }
    },
    badgeAriaLabel() {
      return this.badgeInfo?.tagType ? this.badgeInfo.tagType + ' Product Indicator' : undefined;
    },
  },
  watch: {
    isActive: {
      immediate: true,
      handler(_isActive) {
        if (_isActive) {
          this.$emit('active', _isActive);
        }
      }
    }
  },
  methods: {
    _$t(...args) {
      return t(...args);
    },
  },
  render(h) {
    if (!this.isActive) {
      return null;
    }
    if (this.$slots.default) {
      return this.$slots.default;
    }

    const className = ['auto-expire-badge', this.badgeThemeClass].join(' ');

    return h(
      'span',
      {
        class: className,
        attrs: {
          'aria-label': this.badgeAriaLabel,
          'data-title': this.badgeInfo?.title,
          'data-badgeid': this.badgeId,
        },
      },
      this.badgeLabel,
    );
  },
};
</script>

<style lang="scss">
.auto-expire-badge {
  display: inline-block;
  vertical-align: middle;
  text-transform: uppercase;

  &.badge--plan {
    font-size: 14px;
    color: #fff;
    border-radius: 20px;
    font-weight: 500;
    padding: 2px 8px;
    background: linear-gradient(96.59deg, #493ab7 10.36%, #cf4b0a 89.64%);
  }

  &.badge--feature {
    background: #ededf4;
    font-weight: 900;
    letter-spacing: 0.25px;
    color: #0b5cff;
    font-size: 12px;
    line-height: 14px;
    border-radius: 4px;
    padding: 3px 7px;
  }

  &.badge--comparison {
    display: initial;
    font-size: 12px;
    line-height: 14px;
    color: #0e72ed;
    font-weight: 900;
    position: relative;
    top: -1px;
    background-color: #F2F2F4;
    padding: 2px 4px;
    border-radius: 4px;
  }
}
</style>
